// Generated from shared/ by scripts/sync-shared.mjs. Do not edit here.
// Microphone capture + voice activity detection for the PWAs.
//
// EDIT THIS FILE HERE (shared/audio/). scripts/sync-shared.mjs copies it into
// frontend, podium, visit, lessons-ui and tonelab; the copies are overwritten on each sync.
// Audio Recording Service for PWA
import { MediaRecorder as ExtendableMediaRecorder, register } from 'extendable-media-recorder';
import { connect } from 'extendable-media-recorder-wav-encoder';

// iOS Safari starts every AudioContext suspended and has stricter limits on concurrent
// AudioWorklets. Silero VAD (ONNX via AudioWorklet) competes with the WAV encoder's
// AudioWorklet and causes sample drops and silent audio on iOS. Energy-based VAD is
// used instead, which runs on the existing analyser node without an extra worklet.
function isIOSOrSafari() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  return isIOS || isSafari;
}

export class AudioService {
  constructor() {
    console.log('🔧 AudioService v4.0 - With Silero VAD + Energy-Based Fallback');
    this.audioContext = null;
    this.stream = null;
    this.analyser = null;
    this.dataArray = null;
    this.isRecording = false;
    this.onDataAvailable = null;
    this.onRecordingStateChange = null;
    this.onVolumeChange = null;
    this.onSpeechPause = null;
    this.hasPermission = false;
    this.mediaRecorder = null;
    this.volumeCheckInterval = null;
    this.pauseCheckInterval = null;
    this.encoderRegistered = false;

    // Silero VAD
    this.sileroVAD = null;
    this.sileroInitialized = false;
    this.sileroHasVoice = false;
    // iOS/Safari: disable Silero VAD — its ONNX AudioWorklet competes with the WAV
    // encoder's AudioWorklet, causing sample drops and corrupted audio. Energy-based
    // VAD runs on the existing analyser node and is reliable on all platforms.
    this.useSileroVAD = !isIOSOrSafari();

    // External VAD callbacks — set by callers to react to speech start/end
    this.onSpeechStart = null;
    this.onSpeechEnd = null;

    // VAD bypass: if Silero VAD initializes but never fires onSpeechStart for
    // this many consecutive chunks, assume it is broken and send all audio.
    this.vadBypassThreshold = 4;
    this.vadSilentChunkCount = 0; // consecutive chunks where VAD saw no speech
    this.vadBypassed = false;     // latched true once bypass is triggered

    // Energy-based VAD (used when Silero isn't: iOS/Safari always, and any browser
    // where Silero fails to load, e.g. some Firefox setups). It measures RMS of the
    // waveform (linear amplitude) every 50 ms against a noise floor that adapts all the
    // time, and remembers whether the current chunk window had speech in it.
    //
    // The previous version compared getByteFrequencyData averages (a dB scale, where
    // room noise already reads ~0.25) with a threshold set once from the first second
    // of recording. People who start talking as soon as they tap the mic calibrated the
    // threshold on their own voice (we measured 0.7–0.8, above any speech), so every
    // chunk was dropped and the app reported no speech.
    this.minSpeechRms = 0.006;  // ~ -44 dBFS: never treat quieter than this as speech
    this.maxSpeechRms = 0.03;   // ~ -30 dBFS: a noisy room can't push the bar above this
    this.noiseFloor = 0;        // running RMS noise estimate (0 = not measured yet)
    this.speechThreshold = this.minSpeechRms;
    this.speechHangoverMs = 600; // keep sending briefly after speech so word endings aren't cut
    this.lastVoiceAt = 0;
    this.zeroLevelSince = 0;    // when the analyser started reading exact silence
    this.analyserDead = false;  // true when the graph delivers only zeros (suspended context, dead track)
    this.pauseThreshold = 1000;
    this.lastSpeechTime = 0;
    this.isSpeaking = false;
    this.isCalibrated = false;  // kept for callers; the noise floor now adapts continuously

    // Debug: Track all accumulated audio for the current recording session
    this.debugAccumulatedAudio = [];

    // Track chunk count to identify first chunk with WAV header
    this.chunkCount = 0;

    // Audio format detected from WAV header (populated on first chunk)
    this.audioFormat = null; // { sampleRate, channels, bitDepth }

    // Sticky VAD flag: true if speech was detected at any point during the current chunk window.
    // Reset to false after each chunk is dispatched so the next window starts clean.
    this.speechDetectedInChunk = false;

    // Set to true between mediaRecorder.stop() and the onstop event so the
    // final ondataavailable chunk is not dropped by the isRecording guard.
    this.isFinalizing = false;

    // Verbose tracing is opt-in to avoid flooding DevTools during normal use.
    this.debugEnabled = localStorage.getItem('polyglot-debug-audio') === 'true' ||
      localStorage.getItem('polyglot-debug') === 'true';
  }

  debugLog(...args) {
    if (this.debugEnabled) {
      console.log(...args);
    }
  }

  async checkMicrophonePermission() {
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
      this.hasPermission = permissionStatus.state === 'granted';
      return this.hasPermission;
    } catch (error) {
      console.warn('Permission API not supported');
      return false;
    }
  }

  async requestMicrophonePermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        }
      });

      this.stream = stream;
      this.hasPermission = true;
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      this.hasPermission = false;
      throw new Error('Microphone access denied.');
    }
  }

  async initializeSileroVAD() {
    // Promise-based guard: if init is already in progress, await the same promise
    if (this._sileroInitPromise) {
      return this._sileroInitPromise;
    }
    this._sileroInitPromise = this._doInitializeSileroVAD();
    return this._sileroInitPromise;
  }

  async _doInitializeSileroVAD() {
    try {
      console.log('Initializing Silero VAD...');

      // Check if VAD is loaded globally from CDN
      if (typeof window.vad === 'undefined') {
        throw new Error('Silero VAD library not loaded. Check CDN script tags in index.html');
      }

      const MicVAD = window.vad.MicVAD;

      // Use CDN paths (already loaded via script tags)
      const basePath = 'https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.27/dist/';
      const onnxPath = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/';

      console.log(`Silero VAD using CDN assets`);

      this.sileroVAD = await MicVAD.new({
        // Asset paths from CDN
        baseAssetPath: basePath,
        onnxWASMBasePath: onnxPath,

        // VAD configuration - matches demo defaults
        positiveSpeechThreshold: 0.5,  // Threshold for speech detection
        negativeSpeechThreshold: 0.35, // Threshold for speech end (hysteresis)
        preSpeechPadFrames: 1,         // Frames to prepend before speech
        redemptionFrames: 8,           // Grace period before ending speech
        minSpeechFrames: 3,            // Min consecutive frames for valid speech (filters music!)

        // Use existing audio stream (don't create new one)
        stream: this.stream,

        // Callbacks for real-time VAD
        onSpeechStart: () => {
          this.sileroHasVoice = true;
          this.speechDetectedInChunk = true; // sticky — stays true until chunk is dispatched
          console.log('Silero VAD: Speech started');
          if (this.onSpeechStart) this.onSpeechStart();
        },
        onSpeechEnd: () => {
          this.sileroHasVoice = false;
          console.log(`[${new Date().toISOString().slice(11,23)}] Silero VAD: Speech ended`);
          if (this.onSpeechEnd) this.onSpeechEnd();
        },
        onVADMisfire: () => {
          console.log('Silero VAD: False alarm (misfire)');
        }
      });

      this.sileroInitialized = true;
      console.log('✅ Silero VAD initialized successfully');
    } catch (error) {
      console.warn('Failed to initialize Silero VAD, falling back to energy-based VAD:', error);
      this.sileroInitialized = false;
      this.useSileroVAD = false;
    }
  }

  // Returns the minimum safe chunk interval for the current platform.
  // Without SharedArrayBuffer the WAV encoder uses postMessage, which drops
  // samples under load at 100ms. iOS also captures at 44100 Hz (vs 48000 Hz
  // on Android) making it more sensitive to timing pressure.
  getMinChunkIntervalMs() {
    if (isIOSOrSafari()) return 500;
    if (!self.crossOriginIsolated) return 250;
    return 100;
  }

  async initialize() {
    try {
      if (!self.crossOriginIsolated) {
        console.warn(
          'AudioService: page is not cross-origin isolated — SharedArrayBuffer unavailable. ' +
          'The WAV encoder falls back to postMessage, which may drop samples at 100ms chunk intervals. ' +
          'Add Cross-Origin-Embedder-Policy: require-corp and set Cross-Origin-Opener-Policy: same-origin to fix.'
        );
      }

      // Register WAV encoder once (global registration)
      if (!this.encoderRegistered) {
        console.log('Registering extendable-media-recorder WAV encoder...');
        await register(await connect());
        this.encoderRegistered = true;
        console.log('WAV encoder registered successfully');
      }

      // iOS ends microphone tracks when the PWA goes to the background, a call comes in,
      // or another app takes the mic. Reusing the dead stream records silence forever,
      // so start over with a fresh one.
      if (this.stream && !this.hasLiveTrack()) {
        console.warn('AudioService: microphone track ended; reopening the microphone');
        this.resetCapture();
      }

      if (!this.hasPermission || !this.stream) {
        await this.requestMicrophonePermission();
      }

      if (!this.stream) {
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            // Note: sampleRate is intentionally NOT specified here.
            // Chrome ignores this hint and always captures at the hardware rate
            // (48kHz). Specifying 16000 mis-labels the stream which confuses
            // the Silero VAD worklet. The backend resamples to 16kHz itself.
            channelCount: 1,
          }
        });
      }

      // Initialize Silero VAD after we have the stream (guard against re-init on duplicate calls)
      if (this.useSileroVAD && !this.sileroInitialized) {
        await this.initializeSileroVAD();
      }

      // Initialize Web Audio API (guard — re-creating AudioContext leaks resources)
      if (!this.audioContext) {
        // Do NOT specify sampleRate here. The hardware stream is at 48kHz
        // (regardless of getUserMedia hints). If the AudioContext is created at
        // 16kHz but the stream is at 48kHz, Chrome's analyser node returns all
        // zeros — breaking the energy-based VAD and noise-floor calibration.
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

        // Set up audio analysis for volume detection
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 256;
        const bufferLength = this.analyser.frequencyBinCount;
        this.dataArray = new Uint8Array(bufferLength);

        // A second analyser with a longer window for the energy VAD (~43 ms at 48 kHz).
        this.vadAnalyser = this.audioContext.createAnalyser();
        this.vadAnalyser.fftSize = 2048;
        this.timeData = new Float32Array(this.vadAnalyser.fftSize);

        const source = this.audioContext.createMediaStreamSource(this.stream);
        source.connect(this.analyser);
        source.connect(this.vadAnalyser);

        // iOS moves the context to "interrupted" (calls, Siri, backgrounding) and
        // Safari/Firefox may leave it "suspended"; either way the analysers read zeros.
        this.audioContext.onstatechange = () => {
          this.debugLog(`AudioContext state: ${this.audioContext?.state}`);
          if (this.isRecording) this.resumeContext();
        };
      }
      await this.resumeContext();

      this.stream.getAudioTracks().forEach((track) => {
        track.onended = () => {
          console.warn('AudioService: microphone track ended');
          // Callers that guard on hasPermission will call initialize() again, which reopens it.
          this.hasPermission = false;
        };
      });

      // Initialize MediaRecorder with WAV encoding (guard against duplicate init)
      if (!this.mediaRecorder) {
        this.mediaRecorder = new ExtendableMediaRecorder(this.stream, {
          mimeType: 'audio/wav',
          audioBitsPerSecond: 128000
        });
      }

      console.log(`Using extendable-media-recorder with mimeType: ${this.mediaRecorder.mimeType}`);
      console.log(`Audio stream sample rate: ${this.audioContext.sampleRate} Hz`);

      // Handle data available from MediaRecorder
      this.mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0 && (this.isRecording || this.isFinalizing) && this.onDataAvailable) {
          // Convert Blob to ArrayBuffer
          const arrayBuffer = await event.data.arrayBuffer();

          const isFirstChunk = (this.chunkCount === 0);
          let pcmData;

          if (isFirstChunk && arrayBuffer.byteLength >= 44) {
            // First chunk has WAV header - check and extract PCM
            const view = new DataView(arrayBuffer);
            const riff = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));

            if (riff === 'RIFF') {
              const sampleRate = view.getUint32(24, true);
              const channels = view.getUint16(22, true);
              const bitsPerSample = view.getUint16(34, true);
              this.debugLog(`Chunk #${this.chunkCount}: ${arrayBuffer.byteLength} bytes WAV, ${sampleRate}Hz, ${channels}ch, ${bitsPerSample}-bit`);

              // Store detected format so all subsequent chunks can report it
              this.audioFormat = { sampleRate, channels, bitDepth: bitsPerSample };

              // Send first chunk WITH WAV header so backend can detect sample rate
              pcmData = arrayBuffer;
              this.debugLog(`Sending first chunk WITH WAV header for sample rate detection (${sampleRate}Hz)`);
            } else {
              // Not a valid WAV header, send as-is
              pcmData = arrayBuffer;
            }
          } else {
            // Subsequent chunks are already raw PCM
            pcmData = arrayBuffer;
            this.debugLog(`Chunk #${this.chunkCount}: ${arrayBuffer.byteLength} bytes raw PCM (format: ${JSON.stringify(this.audioFormat)})`);
          }

          this.chunkCount++;

          // Store original for debug purposes
          this.storeDebugAudio(arrayBuffer);

          // Send data to server, including audio format metadata for every chunk
          this.onDataAvailable(pcmData, this.audioFormat);
          // Reset sticky VAD window — next chunk starts fresh
          this.speechDetectedInChunk = false;
          this.energyVoiceInChunk = false;
        }
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
      };

      return true;
    } catch (error) {
      console.error('Failed to initialize audio:', error);
      throw error;
    }
  }

  storeDebugAudio(arrayBuffer) {
    // For debug purposes, store audio chunks
    // Note: Only first chunk has valid WAV header
    // Subsequent chunks may be raw PCM data
    const audioData = new Uint8Array(arrayBuffer);
    this.debugAccumulatedAudio.push(audioData);
  }

  hasLiveTrack() {
    return !!this.stream && this.stream.getAudioTracks().some((t) => t.readyState === 'live');
  }

  /** Resume the AudioContext unless it's already running. Safe to call repeatedly. */
  async resumeContext() {
    const ctx = this.audioContext;
    if (!ctx || ctx.state === 'running' || ctx.state === 'closed') return;
    try {
      await ctx.resume();
      this.debugLog(`AudioContext resumed: ${ctx.state}`);
    } catch (e) {
      console.warn('AudioContext resume failed:', e);
    }
  }

  /** Drop the stream and everything built on it, so initialize() starts fresh. */
  resetCapture() {
    if (this.sileroVAD) {
      try { this.sileroVAD.destroy(); } catch { /* already gone */ }
      this.sileroVAD = null;
      this.sileroInitialized = false;
      this._sileroInitPromise = null;
    }
    this.mediaRecorder = null;
    if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.hasPermission = false;
    if (this.audioContext) {
      this.audioContext.onstatechange = null;
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    this.analyser = null;
    this.vadAnalyser = null;
  }

  startRecording(onAudioData, onVolumeChange, onSpeechPause, chunkIntervalMs = 100) {
    // Scale VAD bypass threshold so it stays ~400ms of silence regardless of chunk rate.
    // 100ms chunks → threshold 4; 1000ms chunks → threshold 1 (1 silent chunk = 1s already)
    this.vadBypassThreshold = Math.max(1, Math.round(400 / chunkIntervalMs));
    if (this.isRecording) return;

    this.onDataAvailable = onAudioData;
    this.onVolumeChange = onVolumeChange || null;
    this.onSpeechPause = onSpeechPause || null;
    this.isRecording = true;
    this.isFinalizing = false;
    this.speechDetectedInChunk = false;
    this.vadSilentChunkCount = 0;
    this.vadBypassed = false;
    this.debugAccumulatedAudio = []; // Reset debug buffer at start of recording
    this.chunkCount = 0; // Reset chunk counter
    this.audioFormat = null; // Reset format — will be re-detected from next WAV header
    this.lastSpeechTime = Date.now();
    this.isSpeaking = false;
    this.lastVoiceAt = 0;
    this.energyVoiceInChunk = false;
    this.zeroLevelSince = 0;
    this.analyserDead = false;

    // Resume AudioContext if suspended (browser autoplay policy suspends it until
    // a user gesture — the mic button click is that gesture)
    // ("interrupted" on iOS, too, not only "suspended".)
    if (this.audioContext) {
      this.debugLog(`AudioContext state before resume: ${this.audioContext.state}`);
      this.resumeContext();
    }

    this.debugLog('Started recording with extendable-media-recorder (WAV format)');
    this.debugLog('Chunk size: 100ms chunks (WAV/PCM stream)');

    if (this.onRecordingStateChange) {
      this.onRecordingStateChange(true);
    }

    // Start Silero VAD if initialized
    if (this.sileroInitialized && this.sileroVAD) {
      try {
        this.sileroVAD.start();
        console.log('✅ Silero VAD started');
      } catch (error) {
        console.warn('Failed to start Silero VAD:', error);
      }
    }

    // Start volume monitoring and voice activity detection
    if (this.analyser && this.dataArray) {
      this.startVolumeMonitoring();
      this.startVoiceActivityDetection();
    }

    // Start MediaRecorder with server-provided timeslice (100ms on GPU, 1000ms on CPU)
    if (this.mediaRecorder && this.mediaRecorder.state === 'inactive') {
      this.mediaRecorder.start(chunkIntervalMs);
      this.debugLog(`MediaRecorder started with ${chunkIntervalMs}ms timeslice`);
    }
  }

  sendAudioOnPause() {
    // Request data from MediaRecorder on pause boundary
    if (this.isRecording && this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.debugLog('Requesting data from MediaRecorder on pause boundary');
      if (typeof this.mediaRecorder.requestData === 'function') {
        this.mediaRecorder.requestData();
      } else {
        this.debugLog('MediaRecorder.requestData is not available');
      }
    }
  }

  stopRecording() {
    this.debugLog('🟢 [AudioService] stopRecording() ENTERED - isRecording:', this.isRecording);

    if (!this.isRecording) {
      this.debugLog('🟢 [AudioService] stopRecording() called but isRecording is already false');
      return;
    }

    this.debugLog('🟢 [AudioService] Stopping recording...');

    // Stop Silero VAD if running
    if (this.sileroInitialized && this.sileroVAD) {
      try {
        this.sileroVAD.pause();
        this.debugLog('🟢 Silero VAD paused');
      } catch (error) {
        console.warn('Failed to pause Silero VAD:', error);
      }
    }

    // Clear intervals first
    if (this.volumeCheckInterval) {
      clearInterval(this.volumeCheckInterval);
      this.volumeCheckInterval = null;
    }

    if (this.pauseCheckInterval) {
      clearInterval(this.pauseCheckInterval);
      this.pauseCheckInterval = null;
    }

    // Stop MediaRecorder (while isRecording is still true so final data can be processed)
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      // Request any remaining data before stopping (if method exists)
      if (this.mediaRecorder.state === 'recording' && typeof this.mediaRecorder.requestData === 'function') {
        try {
          this.mediaRecorder.requestData();
          this.debugLog('🟢 [AudioService] Final requestData() called');
        } catch (error) {
          this.debugLog('🟢 [AudioService] Error calling requestData:', error);
        }
      }

      // Set isFinalizing so the final ondataavailable event (which fires
      // asynchronously after stop()) is not dropped by the isRecording guard.
      this.isFinalizing = true;

      // Move state cleanup to onstop, which fires after the last ondataavailable.
      this.mediaRecorder.onstop = () => {
        this.isRecording = false;
        this.isFinalizing = false;
        if (this.onRecordingStateChange) {
          this.onRecordingStateChange(false);
        }
        this.debugLog('🟢 [AudioService] Recording stopped (onstop)');
      };

      // Stop the MediaRecorder
      try {
        this.mediaRecorder.stop();
        this.debugLog('🟢 [AudioService] MediaRecorder.stop() called');
      } catch (error) {
        console.error('🟢 [AudioService] Error stopping MediaRecorder:', error);
        // stop() threw — clean up immediately since onstop won't fire
        this.isRecording = false;
        this.isFinalizing = false;
        if (this.onRecordingStateChange) {
          this.onRecordingStateChange(false);
        }
      }
    } else {
      // No MediaRecorder to stop, just update state
      this.isRecording = false;

      if (this.onRecordingStateChange) {
        this.onRecordingStateChange(false);
      }

      this.debugLog('🟢 [AudioService] Recording stopped (no active MediaRecorder)');
    }
  }

  floatArrayToWav(audioData, sampleRate) {
    const length = audioData.length;
    const buffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(buffer);

    // WAV header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, length * 2, true);

    // Convert float samples to 16-bit PCM
    const offset = 44;
    for (let i = 0; i < length; i++) {
      const sample = Math.max(-1, Math.min(1, audioData[i]));
      view.setInt16(offset + i * 2, sample * 0x7FFF, true);
    }

    return buffer;
  }

  startVolumeMonitoring() {
    this.volumeCheckInterval = setInterval(() => {
      if (this.analyser && this.dataArray && this.onVolumeChange) {
        this.analyser.getByteFrequencyData(this.dataArray);

        let sum = 0;
        for (let i = 0; i < this.dataArray.length; i++) {
          sum += this.dataArray[i];
        }
        const average = sum / this.dataArray.length;
        const volume = average / 255; // Normalize to 0-1

        this.onVolumeChange(volume);
      }
    }, 50); // Check volume every 50ms
  }

  /** RMS of the latest ~43 ms of audio, 0..1 (linear amplitude, not dB). */
  measureRms() {
    if (!this.vadAnalyser || !this.timeData) return 0;
    this.vadAnalyser.getFloatTimeDomainData(this.timeData);
    let sum = 0;
    for (let i = 0; i < this.timeData.length; i++) sum += this.timeData[i] * this.timeData[i];
    return Math.sqrt(sum / this.timeData.length);
  }

  /**
   * One energy-VAD step: update the noise floor and decide whether this instant is
   * speech. Called every 50 ms while recording.
   */
  updateEnergyVad(now = Date.now()) {
    const rms = this.measureRms();

    // Exact zeros mean the graph isn't delivering audio at all (suspended or
    // interrupted context, ended track). Real microphones always have some noise.
    if (rms === 0) {
      if (!this.zeroLevelSince) this.zeroLevelSince = now;
      if (!this.analyserDead && now - this.zeroLevelSince > 1500) {
        this.analyserDead = true;
        console.warn('AudioService: analyser reads silence; sending audio without VAD', this.audioContext?.state);
        this.resumeContext();
      }
      return false;
    }
    this.zeroLevelSince = 0;
    this.analyserDead = false;

    // Noise floor: drops quickly to quieter readings, creeps up slowly (~20 s), so it
    // follows the room but speech can't drag it up.
    if (!this.noiseFloor || rms < this.noiseFloor) {
      this.noiseFloor = this.noiseFloor ? this.noiseFloor * 0.7 + rms * 0.3 : rms;
    } else {
      this.noiseFloor += (rms - this.noiseFloor) * 0.0025;
    }
    this.speechThreshold = Math.min(this.maxSpeechRms, Math.max(this.minSpeechRms, this.noiseFloor * 3));

    const voiced = rms > this.speechThreshold;
    if (voiced) {
      this.lastVoiceAt = now;
      this.energyVoiceInChunk = true; // sticky until the chunk is dispatched
    }
    return voiced;
  }

  startVoiceActivityDetection() {
    this.pauseCheckInterval = setInterval(() => {
      if (!this.vadAnalyser) return;
      const now = Date.now();
      const voiced = this.updateEnergyVad(now);

      if (voiced) {
        if (!this.isSpeaking) {
          this.debugLog(`Speech started (rms threshold ${this.speechThreshold.toFixed(4)})`);
          this.isSpeaking = true;
        }
        this.lastSpeechTime = now;
      } else if (this.isSpeaking) {
        const pauseDuration = now - this.lastSpeechTime;
        if (pauseDuration > this.pauseThreshold) {
          this.debugLog(`Speech pause detected: ${pauseDuration}ms`);
          this.isSpeaking = false;
          if (this.onSpeechPause) {
            this.onSpeechPause(pauseDuration);
          }
        }
      }
    }, 50);
  }

  getAccumulatedAudio() {
    // Combine all WAV chunks for debug playback
    if (this.debugAccumulatedAudio.length === 0) {
      return null;
    }

    // WAV chunks can be concatenated by combining their data sections
    // For simplicity, we'll just combine all the bytes
    // Note: This creates a valid multi-chunk WAV that most players can handle
    const totalLength = this.debugAccumulatedAudio.reduce((sum, chunk) => sum + chunk.length, 0);
    const combined = new Uint8Array(totalLength);
    let offset = 0;

    for (const chunk of this.debugAccumulatedAudio) {
      combined.set(chunk, offset);
      offset += chunk.length;
    }

    // Estimate duration (rough calculation based on total bytes)
    // WAV format: 44 bytes header + 2 bytes per sample at 16kHz
    const estimatedSamples = (totalLength - (44 * this.debugAccumulatedAudio.length)) / 2;
    const duration = estimatedSamples / 16000;

    return {
      audioData: combined,
      duration: duration,
      sampleRate: 16000,
      mimeType: 'audio/wav'
    };
  }

  cleanup() {
    this.stopRecording();

    // Cleanup Silero VAD
    if (this.sileroVAD) {
      try {
        this.sileroVAD.destroy();
        console.log('Silero VAD destroyed');
      } catch (error) {
        console.warn('Error destroying Silero VAD:', error);
      }
      this.sileroVAD = null;
      this.sileroInitialized = false;
    }

    if (this.mediaRecorder) {
      this.mediaRecorder = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  // ========== Enhanced Energy-Based VAD Methods ==========

  /**
   * Kept so existing callers keep working. The noise floor now adapts continuously in
   * updateEnergyVad(); a one-off calibration taken while the user was already talking
   * was what made speech look like noise.
   */
  async calibrateNoiseFloor() {
    this.isCalibrated = true;
  }

  hasVoiceActivity() {
    // Use Silero VAD if initialized.
    // Also include speechDetectedInChunk (sticky flag) so a chunk is never
    // dropped just because onSpeechEnd fired before the 1-second boundary.
    if (this.sileroInitialized && this.sileroVAD) {
      const hasVoice = this.sileroHasVoice || this.speechDetectedInChunk;

      // Bypass logic: if Silero VAD keeps returning false for too many
      // consecutive chunks it is likely broken (suspended AudioContext, stream
      // issue, etc.). After vadBypassThreshold silent chunks, latch bypass mode
      // and send all audio so the user isn't silently dropped.
      if (!hasVoice) {
        this.vadSilentChunkCount++;
        if (!this.vadBypassed && this.vadSilentChunkCount >= this.vadBypassThreshold) {
          this.vadBypassed = true;
          console.warn(`[${new Date().toISOString().slice(11,23)}] ⚠️ VAD bypass activated: Silero VAD produced no speech in ${this.vadBypassThreshold} consecutive chunks. Sending all audio. (sileroHasVoice=${this.sileroHasVoice}, speechDetectedInChunk=${this.speechDetectedInChunk})`);  
          // Treat bypass activation as speech-end: notify server so it can
          // finalize any accumulated draft without waiting for the inactivity timer.
          if (this.onSpeechEnd) this.onSpeechEnd();
        }
      } else {
        this.vadSilentChunkCount = 0;
      }

      if (this.vadBypassed) {
        return true; // bypass — send everything
      }

      this.debugLog(`Silero VAD check: sileroHasVoice=${this.sileroHasVoice}, speechDetectedInChunk=${this.speechDetectedInChunk}, silentChunks=${this.vadSilentChunkCount}`);
      return hasVoice;
    }

    // Energy VAD: was there speech at any point in this chunk window (or very
    // recently, so word endings go too)? The 50 ms loop sets the sticky flag.
    if (!this.vadAnalyser) return true; // no analyser: let the backend's VAD decide
    if (this.analyserDead) return true;  // can't measure: send rather than drop speech
    const recent = this.lastVoiceAt && Date.now() - this.lastVoiceAt < this.speechHangoverMs;
    const hasVoice = this.energyVoiceInChunk || recent;
    this.debugLog(`Energy VAD: ${hasVoice ? 'VOICE' : 'quiet'} floor=${this.noiseFloor.toFixed(4)} threshold=${this.speechThreshold.toFixed(4)}`);
    return !!hasVoice;
  }

  calculateVolume(dataArray) {
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    return sum / dataArray.length / 255;
  }
}

export const audioService = new AudioService();
